// The feedback & bug-report board's rendering/interaction logic - the list,
// detail, and new/edit form views, and everything that wires clicks on them
// to an API. Shared between the app's own sidebar modal ('user' mode,
// core/feedback.js as the api, device-id ownership) and the /admin/feedback
// page ('admin' mode, core/admin-feedback.js as the api, HTTP Basic Auth
// instead of ownership) - see mountFeedbackBoard() below. Neither caller's
// DOM structure or API wiring leaks in here; this only ever touches the
// container element it's handed and calls whatever functions `api` exposes.
//
// Like core/menu.js and components/keypad.js, state is module-scoped rather
// than instance-scoped, since only one board is ever mounted per page (the
// modal's content on the main app, or the admin page's whole body).

let state = null;

function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function relativeTime(ms) {
    const diffMin = Math.round((Date.now() - ms) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return new Date(ms).toLocaleDateString();
}

function formatDate(ms) {
    return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function statusLabel(status) {
    return { open: 'Open', planned: 'Planned', done: 'Done', wont_fix: 'Won\'t fix' }[status] || status;
}

// One line summarizing unread events on an item, e.g. "Marked Planned · 2
// new replies". Only the latest status matters if it changed more than once.
function describeUpdates(events) {
    const parts = [];
    const statuses = events.filter((e) => e.kind === 'status');
    if (statuses.length > 0) parts.push(`Marked ${statusLabel(statuses[statuses.length - 1].detail)}`);
    const replyCount = events.filter((e) => e.kind === 'reply').length;
    if (replyCount > 0) parts.push(replyCount === 1 ? 'New reply' : `${replyCount} new replies`);
    const lastCloseEvent = events.filter((e) => e.kind === 'closed' || e.kind === 'reopened').pop();
    if (lastCloseEvent) parts.push(lastCloseEvent.kind === 'closed' ? 'Closed' : 'Reopened');
    return parts.join(' · ');
}

// The API calls behind renderList()/openDetail() usually resolve well under
// this - writing "Loading..." into the DOM immediately would just flash it
// on screen for one frame and then replace it, which reads as a flicker
// rather than useful feedback. Delaying it means a fast response never
// touches the DOM with a loading state at all; the caller gets back a
// cancel function to call once the real response is in, whether or not the
// delayed write ever fired.
function showLoadingAfterDelay(el, delayMs = 500) {
    const timer = setTimeout(() => {
        el.innerHTML = '<div class="no-games">Loading&hellip;</div>';
    }, delayMs);
    return () => clearTimeout(timer);
}

// Mounts (or, called again, re-shows) the board into containerEl. Filter/
// sort/status-filter persist across repeat calls (reopening the sidebar
// modal keeps whatever you had it on) - only the error message is cleared,
// same as the old showFeedbackModal() did. `mode` is 'user' (vote buttons,
// mine-gated edit/delete, can create new posts) or 'admin' (no voting or
// creating, edit/delete any item, a status control, and an active/deleted/
// all filter - see server/feedback.js's admin* functions this calls into
// via `api`).
//
// initialItemId/onNavigate are how a caller (currently just
// admin-feedback-entry.js) hooks in #-hash routing without this module
// knowing anything about URLs itself: initialItemId opens straight to that
// item's detail view instead of the list on first mount, and onNavigate(view)
// - view is {view:'list'} or {view:'detail', id} - fires every time the
// board actually lands on one of those two views, so the caller can mirror
// it into location.hash. Neither is used in 'user' mode - the sidebar modal
// has no URL of its own to route.
//
// `updates` (user mode only, optional) is { getForItem(id), markSeen(id) } -
// see core/feedback-updates.js. Cards with unread changes get an "Updated"
// chip, and opening an item's detail view shows what changed and marks it
// seen. The admin page just doesn't pass it.
export function mountFeedbackBoard({ containerEl, api, mode, initialItemId, onNavigate, updates, scrollToUpdated } = {}) {
    if (!state || state.containerEl !== containerEl) {
        state = {
            containerEl, api, mode, onNavigate: onNavigate || null, updates: updates || null, detailUpdates: [],
            filter: 'all', sort: 'top', statusFilter: 'active',
            items: [], detailItem: null, editingItem: null,
            confirmingDelete: false, confirmingHardDelete: false, error: null,
            editingReplyId: null, confirmingDeleteReplyId: null, replyError: null, choosingClose: false,
        };
    } else {
        state.error = null;
        if (onNavigate) state.onNavigate = onNavigate;
    }
    // One-shot: the next list render scrolls the first card with unread
    // changes into view (the app's "you have updates" toast opens the board
    // this way). The filter is reset so a leftover Bugs/Features tab can't
    // hide the very card being pointed at.
    state.scrollToUpdated = !!scrollToUpdated;
    if (scrollToUpdated) state.filter = 'all';
    if (initialItemId) openDetail(initialItemId);
    else renderList();
}

function notifyNavigate(view) {
    if (state.onNavigate) state.onNavigate(view);
}

async function renderList() {
    const { containerEl, api, mode } = state;
    const stopLoading = showLoadingAfterDelay(containerEl);
    // 'closed' is a tab of its own (already-closed items of any type), not a
    // type - so it never becomes the `type` filter, and in admin mode it
    // overrides the Show row instead of combining with it.
    const showingClosed = state.filter === 'closed';
    try {
        state.items = await api.listFeedback({
            type: state.filter === 'all' || showingClosed ? undefined : state.filter,
            sort: state.sort,
            ...(mode === 'admin'
                ? { status: showingClosed ? 'closed' : state.statusFilter }
                : { closed: showingClosed })
        });
    } catch (e) {
        stopLoading();
        containerEl.innerHTML = '<div class="no-games">Couldn\'t load feedback right now</div>';
        return;
    }
    stopLoading();
    notifyNavigate({ view: 'list' });

    const tab = (value, label, extraClass = '') =>
        `<button class="feedback-tab ${extraClass} ${state.filter === value ? 'active' : ''}" data-filter="${value}">${label}</button>`;
    const sortOption = (value, label) =>
        `<button class="feedback-sort-option ${state.sort === value ? 'active' : ''}" data-sort="${value}">${label}</button>`;
    const statusFilterOption = (value, label) =>
        `<button class="feedback-sort-option ${state.statusFilter === value ? 'active' : ''}" data-status-filter="${value}">${label}</button>`;

    const cardsHTML = state.items.length === 0
        ? (showingClosed
            ? '<div class="no-games">Nothing has been closed yet</div>'
            : '<div class="no-games">No feedback yet &mdash; be the first to add some</div>')
        : state.items.map((item) => {
            const unreadEvents = state.updates ? state.updates.getForItem(item.id) : [];
            return `
            <div class="feedback-card" data-open-id="${item.id}">
                ${item.replyCount > 0 ? `
                    <div class="feedback-card-replies" title="${item.replyCount} ${item.replyCount === 1 ? 'reply' : 'replies'}">
                        <i class="fa-solid fa-message"></i>
                        <span class="feedback-card-replies-count">${item.replyCount}</span>
                    </div>
                ` : ''}
                ${mode === 'admin' ? `
                    <div class="feedback-vote">
                        <span class="feedback-vote-count">${item.voteCount}</span>
                    </div>
                ` : `
                    <div class="feedback-vote">
                        <button class="feedback-vote-btn up ${item.myVote === 'up' ? 'active' : ''}" data-vote-id="${item.id}" data-direction="up" aria-label="Upvote"><i class="fa-solid fa-chevron-up"></i></button>
                        <span class="feedback-vote-count">${item.voteCount}</span>
                        <button class="feedback-vote-btn down ${item.myVote === 'down' ? 'active' : ''}" data-vote-id="${item.id}" data-direction="down" aria-label="Downvote"><i class="fa-solid fa-chevron-down"></i></button>
                    </div>
                `}
                <div class="feedback-card-body">
                    <div class="feedback-badges">
                        <span class="feedback-badge feedback-badge-${item.type}">${item.type}</span>
                        <span class="feedback-badge feedback-badge-status feedback-badge-status-${item.status}">${statusLabel(item.status)}</span>
                        ${item.deletedAt ? '<span class="feedback-badge-mine">&middot; Deleted</span>' : ''}
                        ${mode === 'admin' && item.isClosed ? '<span class="feedback-badge-mine">&middot; Closed</span>' : ''}
                        ${mode === 'admin' && item.closedAt && !item.isClosed ? `<span class="feedback-badge-mine">&middot; Closes ${formatDate(item.closedAt)}</span>` : ''}
                        ${mode === 'user' && item.mine ? '<span class="feedback-badge-mine">&middot; You</span>' : ''}
                        ${unreadEvents.length > 0 ? '<span class="feedback-badge feedback-badge-updated">Updated</span>' : ''}
                    </div>
                    <div class="feedback-card-title">${escapeHtml(item.title)}</div>
                    ${unreadEvents.length > 0 ? `<div class="feedback-card-updates">${describeUpdates(unreadEvents)}</div>` : ''}
                    <div class="feedback-card-snippet">${escapeHtml(item.body)}</div>
                    <div class="feedback-card-time">${relativeTime(item.createdAt)}</div>
                </div>
            </div>
        `;
        }).join('');

    containerEl.innerHTML = `
        <div class="feedback-header">
            <h3 class="modal-title">Feedback &amp; Bugs</h3>
        </div>
        <div class="feedback-subtitle">Report a bug, request a feature, and vote on what matters most.</div>
        <div class="feedback-tabs">
            ${tab('all', 'All')}
            ${tab('bug', 'Bugs')}
            ${tab('feature', 'Features')}
            ${tab('closed', 'Closed', 'feedback-tab-closed')}
        </div>
        ${mode === 'admin' && !showingClosed ? `
            <div class="feedback-sort">
                <span class="feedback-sort-label">Show</span>
                ${statusFilterOption('active', 'Active')}
                ${statusFilterOption('deleted', 'Deleted')}
                ${statusFilterOption('all', 'All')}
            </div>
        ` : ''}
        ${state.items.length > 0 ? `
            <div class="feedback-sort">
                <span class="feedback-sort-label">Sort</span>
                ${sortOption('top', 'Top voted')}
                ${sortOption('new', 'Newest')}
            </div>
        ` : ''}
        <div class="feedback-list">${cardsHTML}</div>
        ${mode === 'user' ? `
            <div class="feedback-footer">
                <button class="feedback-new-btn" data-new><i class="fa-solid fa-plus"></i> New feedback</button>
            </div>
        ` : ''}
    `;

    const newBtn = containerEl.querySelector('[data-new]');
    if (newBtn) newBtn.addEventListener('click', () => openForm());

    containerEl.querySelectorAll('[data-filter]').forEach((el) => {
        el.addEventListener('click', () => { state.filter = el.dataset.filter; renderList(); });
    });
    containerEl.querySelectorAll('[data-sort]').forEach((el) => {
        el.addEventListener('click', () => { state.sort = el.dataset.sort; renderList(); });
    });
    containerEl.querySelectorAll('[data-status-filter]').forEach((el) => {
        el.addEventListener('click', () => { state.statusFilter = el.dataset.statusFilter; renderList(); });
    });
    containerEl.querySelectorAll('[data-vote-id]').forEach((el) => {
        el.addEventListener('click', (e) => {
            e.stopPropagation(); // inside a .feedback-card, which also opens the detail view on click
            voteFromList(el.dataset.voteId, el.dataset.direction);
        });
    });
    containerEl.querySelectorAll('[data-open-id]').forEach((el) => {
        el.addEventListener('click', () => openDetail(el.dataset.openId));
    });

    if (state.scrollToUpdated) {
        state.scrollToUpdated = false;
        const firstUpdated = containerEl.querySelector('.feedback-badge-updated');
        if (firstUpdated) {
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            firstUpdated.closest('.feedback-card').scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
    }
}

async function voteFromList(id, direction) {
    try {
        await state.api.voteFeedbackItem(id, direction);
        await renderList();
    } catch (e) {
        // A vote failing (offline, rate limit edge case) isn't worth a modal
        // of its own - the list just doesn't update, same as a failed track().
    }
}

async function openDetail(id) {
    state.confirmingDelete = false;
    state.confirmingHardDelete = false;
    state.choosingClose = false;
    state.editingReplyId = null;
    state.confirmingDeleteReplyId = null;
    state.replyError = null;
    state.detailUpdates = [];
    const { containerEl, api } = state;
    const stopLoading = showLoadingAfterDelay(containerEl);
    try {
        state.detailItem = await api.getFeedbackItem(id);
    } catch (e) {
        stopLoading();
        containerEl.innerHTML = '<div class="no-games">Couldn\'t load that item</div>';
        return;
    }
    stopLoading();
    if (state.updates) {
        // Kept for this visit's "What's new" line, then cleared so the
        // card's chip and the sidebar badge go away.
        state.detailUpdates = state.updates.getForItem(state.detailItem.id);
        state.updates.markSeen(state.detailItem.id);
    }
    notifyNavigate({ view: 'detail', id: state.detailItem.id });
    renderDetail();
}

// Admin only. Closing is a date, not a status change (a closed item keeps
// whatever status it had) - a close that hasn't reached its date yet is a
// scheduled one, and just as undoable as one that has. Not shown on a
// soft-deleted item: it's already out of every list.
function renderClosePanel(item) {
    if (item.deletedAt) return '';
    let body;
    if (item.closedAt) {
        body = `
            <div class="feedback-close-note">${item.isClosed ? 'Closed' : 'Scheduled to close'} on ${formatDate(item.closedAt)}</div>
            <button class="feedback-btn-secondary" data-reopen>${item.isClosed ? 'Reopen' : 'Cancel scheduled close'}</button>
        `;
    } else if (state.choosingClose) {
        body = `
            <div class="feedback-close-options">
                <button class="feedback-btn-secondary" data-close-when="now">Now</button>
                <button class="feedback-btn-secondary" data-close-when="week">In 1 week</button>
                <button class="feedback-btn-secondary" data-close-when="month">In 1 month</button>
            </div>
            <button class="feedback-cancel-link" data-cancel-choose-close>Cancel</button>
        `;
    } else {
        body = '<button class="feedback-btn-secondary" data-choose-close><i class="fa-solid fa-box-archive"></i> Close item&hellip;</button>';
    }
    return `
        <div class="feedback-field">
            <span class="feedback-field-label">Closing</span>
            ${body}
        </div>
    `;
}

function renderDetail() {
    const { containerEl, mode } = state;
    const item = state.detailItem;

    const voteHTML = mode === 'admin'
        ? `
            <div class="feedback-vote-pill">
                <span class="feedback-vote-count">${item.voteCount}</span>
            </div>
        `
        : `
            <div class="feedback-vote-pill">
                <button class="feedback-vote-btn down ${item.myVote === 'down' ? 'active' : ''}" data-vote-detail data-direction="down" aria-label="Downvote"><i class="fa-solid fa-chevron-down"></i></button>
                <span class="feedback-vote-count">${item.voteCount}</span>
                <button class="feedback-vote-btn up ${item.myVote === 'up' ? 'active' : ''}" data-vote-detail data-direction="up" aria-label="Upvote"><i class="fa-solid fa-chevron-up"></i></button>
            </div>
        `;

    let actionsHTML;
    if (mode === 'admin') {
        actionsHTML = `
            <div class="feedback-field">
                <label class="feedback-field-label" for="feedbackStatusSelect">Status</label>
                <select id="feedbackStatusSelect" class="feedback-input" data-status-select>
                    ${['open', 'planned', 'done', 'wont_fix'].map((s) => `<option value="${s}" ${item.status === s ? 'selected' : ''}>${statusLabel(s)}</option>`).join('')}
                </select>
            </div>
            ${renderClosePanel(item)}
            ${item.deletedAt ? `
                <div class="feedback-detail-actions">
                    <button class="feedback-btn-secondary" data-restore>Restore</button>
                    <button class="btn-danger" data-confirm-hard-delete>Delete permanently</button>
                </div>
                ${state.confirmingHardDelete ? `
                    <div class="feedback-confirm">
                        <div class="feedback-confirm-title">Permanently delete this feedback?</div>
                        <div class="feedback-confirm-body">This removes it (and its votes) completely - it can't be restored afterward.</div>
                        <div class="feedback-confirm-actions">
                            <button class="feedback-btn-secondary" data-cancel-hard-delete>Cancel</button>
                            <button class="btn-danger" data-do-hard-delete>Delete permanently</button>
                        </div>
                    </div>
                ` : ''}
            ` : `
                <div class="feedback-detail-actions">
                    <button class="feedback-btn-secondary" data-edit-id="${item.id}">Edit</button>
                    <button class="btn-danger" data-confirm-delete>Delete</button>
                </div>
                ${state.confirmingDelete ? `
                    <div class="feedback-confirm">
                        <div class="feedback-confirm-title">Delete this feedback?</div>
                        <div class="feedback-confirm-body">This can't be undone.</div>
                        <div class="feedback-confirm-actions">
                            <button class="feedback-btn-secondary" data-cancel-delete>Cancel</button>
                            <button class="btn-danger" data-do-delete="${item.id}">Delete</button>
                        </div>
                    </div>
                ` : ''}
            `}
        `;
    } else {
        actionsHTML = item.mine
            ? `
                <div class="feedback-detail-actions">
                    <button class="feedback-btn-secondary" data-edit-id="${item.id}">Edit</button>
                    <button class="btn-danger" data-confirm-delete>Delete</button>
                </div>
                ${state.confirmingDelete ? `
                    <div class="feedback-confirm">
                        <div class="feedback-confirm-title">Delete this feedback?</div>
                        <div class="feedback-confirm-body">This can't be undone.</div>
                        <div class="feedback-confirm-actions">
                            <button class="feedback-btn-secondary" data-cancel-delete>Cancel</button>
                            <button class="btn-danger" data-do-delete="${item.id}">Delete</button>
                        </div>
                    </div>
                ` : ''}
            `
            : '<div class="feedback-owner-note">Only the original poster can edit or remove this.</div>';
    }

    containerEl.innerHTML = `
        <div class="feedback-back-row">
            <button class="feedback-back-btn" data-back aria-label="Back"><i class="fa-solid fa-chevron-left"></i></button>
            <h3 class="modal-title" style="margin-bottom: 0;">Feedback</h3>
        </div>
        <div class="feedback-scroll">
            <div class="feedback-badges">
                <span class="feedback-badge feedback-badge-${item.type}">${item.type}</span>
                <span class="feedback-badge feedback-badge-status feedback-badge-status-${item.status}">${statusLabel(item.status)}</span>
            </div>
            ${state.detailUpdates.length > 0 ? `
                <div class="feedback-whats-new"><i class="fa-solid fa-bell"></i> ${describeUpdates(state.detailUpdates)}</div>
            ` : ''}
            <div class="feedback-detail-title">${escapeHtml(item.title)}</div>
            <div class="feedback-detail-meta">
                Submitted ${relativeTime(item.createdAt)}${mode === 'user' && item.mine ? ' &middot; You posted this' : ''}${item.deletedAt ? ` &middot; Deleted ${relativeTime(item.deletedAt)}` : ''}
            </div>
            <div class="feedback-detail-body">${escapeHtml(item.body)}</div>
            ${voteHTML}
            ${actionsHTML}
            ${renderRepliesSection(item)}
        </div>
    `;

    containerEl.querySelector('[data-back]').addEventListener('click', renderList);
    wireReplies(containerEl, item);

    containerEl.querySelectorAll('[data-vote-detail]').forEach((el) => {
        el.addEventListener('click', async () => {
            try {
                state.detailItem = await state.api.voteFeedbackItem(item.id, el.dataset.direction);
                renderDetail();
            } catch (e) {}
        });
    });

    const statusSelect = containerEl.querySelector('[data-status-select]');
    if (statusSelect) statusSelect.addEventListener('change', async () => {
        try {
            state.detailItem = await state.api.setFeedbackStatus(item.id, statusSelect.value);
            renderDetail();
        } catch (e) {}
    });

    const chooseCloseBtn = containerEl.querySelector('[data-choose-close]');
    if (chooseCloseBtn) chooseCloseBtn.addEventListener('click', () => { state.choosingClose = true; renderDetail(); });
    const cancelChooseCloseBtn = containerEl.querySelector('[data-cancel-choose-close]');
    if (cancelChooseCloseBtn) cancelChooseCloseBtn.addEventListener('click', () => { state.choosingClose = false; renderDetail(); });
    containerEl.querySelectorAll('[data-close-when]').forEach((el) => {
        el.addEventListener('click', async () => {
            try {
                state.detailItem = await state.api.closeFeedbackItem(item.id, el.dataset.closeWhen);
                state.choosingClose = false;
                renderDetail();
            } catch (e) {}
        });
    });
    const reopenBtn = containerEl.querySelector('[data-reopen]');
    if (reopenBtn) reopenBtn.addEventListener('click', async () => {
        try {
            state.detailItem = await state.api.reopenFeedbackItem(item.id);
            renderDetail();
        } catch (e) {}
    });

    const editBtn = containerEl.querySelector('[data-edit-id]');
    if (editBtn) editBtn.addEventListener('click', () => openForm(item));

    const confirmBtn = containerEl.querySelector('[data-confirm-delete]');
    if (confirmBtn) confirmBtn.addEventListener('click', () => { state.confirmingDelete = true; renderDetail(); });
    const cancelBtn = containerEl.querySelector('[data-cancel-delete]');
    if (cancelBtn) cancelBtn.addEventListener('click', () => { state.confirmingDelete = false; renderDetail(); });
    const doDeleteBtn = containerEl.querySelector('[data-do-delete]');
    if (doDeleteBtn) doDeleteBtn.addEventListener('click', async () => {
        try {
            await state.api.deleteFeedbackItem(item.id);
            await renderList();
        } catch (e) {
            state.error = e.message;
            renderDetail();
        }
    });

    const restoreBtn = containerEl.querySelector('[data-restore]');
    if (restoreBtn) restoreBtn.addEventListener('click', async () => {
        try {
            state.detailItem = await state.api.restoreFeedbackItem(item.id);
            renderDetail();
        } catch (e) {}
    });
    const confirmHardBtn = containerEl.querySelector('[data-confirm-hard-delete]');
    if (confirmHardBtn) confirmHardBtn.addEventListener('click', () => { state.confirmingHardDelete = true; renderDetail(); });
    const cancelHardBtn = containerEl.querySelector('[data-cancel-hard-delete]');
    if (cancelHardBtn) cancelHardBtn.addEventListener('click', () => { state.confirmingHardDelete = false; renderDetail(); });
    const doHardDeleteBtn = containerEl.querySelector('[data-do-hard-delete]');
    if (doHardDeleteBtn) doHardDeleteBtn.addEventListener('click', async () => {
        try {
            await state.api.hardDeleteFeedbackItem(item.id);
            await renderList();
        } catch (e) {
            state.error = e.message;
            renderDetail();
        }
    });
}

// Any device can reply to any item (not just its own - see
// server/feedback.js's createReply). A reply gets a "Reporter" chip when
// it's from the same device as the item's original poster (reply.isReporter,
// computed server-side), and an "Admin" chip plus a distinct background
// (.feedback-reply-admin) when it was posted from the admin panel
// (reply.isAdmin). Edit is author-only (user mode, reply.mine); delete is
// author-only in user mode but any-reply in admin mode (moderation - see
// server/feedback.js's adminSoftDeleteReply having no ownership check).
function renderReply(reply) {
    const isEditingThis = state.editingReplyId === reply.id;
    const isConfirmingDelete = state.confirmingDeleteReplyId === reply.id;
    const canEdit = state.mode === 'user' && reply.mine;
    const canDelete = (state.mode === 'user' && reply.mine) || state.mode === 'admin';

    const bodyOrEditHTML = isEditingThis ? `
        <textarea class="feedback-textarea" maxlength="1000" data-reply-edit-input="${reply.id}">${escapeHtml(reply.body)}</textarea>
        ${state.replyError ? `<div class="feedback-error">${escapeHtml(state.replyError)}</div>` : ''}
        <div class="feedback-form-actions">
            <button class="feedback-btn-primary" data-save-reply="${reply.id}">Save</button>
            <button class="feedback-cancel-link" data-cancel-edit-reply>Cancel</button>
        </div>
    ` : `<div class="feedback-reply-body">${escapeHtml(reply.body)}</div>`;

    const actionsHTML = !isEditingThis && (canEdit || canDelete) ? `
        <div class="feedback-reply-actions">
            ${canEdit ? `<button class="feedback-reply-action" data-edit-reply="${reply.id}">Edit</button>` : ''}
            ${canDelete ? `<button class="feedback-reply-action feedback-reply-action-danger" data-confirm-delete-reply="${reply.id}">Delete</button>` : ''}
        </div>
    ` : '';

    const confirmHTML = isConfirmingDelete ? `
        <div class="feedback-confirm">
            <div class="feedback-confirm-title">Delete this reply?</div>
            <div class="feedback-confirm-actions">
                <button class="feedback-btn-secondary" data-cancel-delete-reply>Cancel</button>
                <button class="btn-danger" data-do-delete-reply="${reply.id}">Delete</button>
            </div>
        </div>
    ` : '';

    return `
        <div class="feedback-reply ${reply.isAdmin ? 'feedback-reply-admin' : ''}">
            <div class="feedback-reply-header">
                ${reply.isReporter ? '<span class="feedback-reply-chip feedback-reply-chip-reporter">Reporter</span>' : ''}
                ${reply.isAdmin ? '<span class="feedback-reply-chip feedback-reply-chip-admin">Admin</span>' : ''}
                <span class="feedback-reply-time">${relativeTime(reply.createdAt)}</span>
            </div>
            ${bodyOrEditHTML}
            ${actionsHTML}
            ${confirmHTML}
        </div>
    `;
}

// No reply composer on a soft-deleted item - nothing to discuss on a post
// that's been removed.
function renderRepliesSection(item) {
    const repliesHTML = (item.replies || []).length === 0
        ? '<div class="feedback-no-replies">No replies yet.</div>'
        : item.replies.map(renderReply).join('');

    const composerHTML = !item.deletedAt ? `
        <div class="feedback-reply-composer">
            <textarea class="feedback-textarea" maxlength="1000" placeholder="Add a reply..." data-reply-input></textarea>
            ${!state.editingReplyId && state.replyError ? `<div class="feedback-error">${escapeHtml(state.replyError)}</div>` : ''}
            <button class="feedback-btn-secondary btn-block" data-submit-reply>Reply</button>
        </div>
    ` : '';

    return `
        <div class="feedback-replies">
            <div class="feedback-field-label">Replies</div>
            <div class="feedback-replies-list">${repliesHTML}</div>
            ${composerHTML}
        </div>
    `;
}

function wireReplies(containerEl, item) {
    const replyInput = containerEl.querySelector('[data-reply-input]');
    const submitReplyBtn = containerEl.querySelector('[data-submit-reply]');
    if (submitReplyBtn) submitReplyBtn.addEventListener('click', async () => {
        const body = replyInput.value.trim();
        try {
            state.detailItem = await state.api.createReply(item.id, body);
            state.replyError = null;
            renderDetail();
        } catch (e) {
            state.replyError = e.message;
            renderDetail();
        }
    });

    containerEl.querySelectorAll('[data-edit-reply]').forEach((el) => {
        el.addEventListener('click', () => {
            state.editingReplyId = Number(el.dataset.editReply);
            state.replyError = null;
            renderDetail();
        });
    });
    containerEl.querySelectorAll('[data-cancel-edit-reply]').forEach((el) => {
        el.addEventListener('click', () => {
            state.editingReplyId = null;
            state.replyError = null;
            renderDetail();
        });
    });
    containerEl.querySelectorAll('[data-save-reply]').forEach((el) => {
        el.addEventListener('click', async () => {
            const replyId = Number(el.dataset.saveReply);
            const input = containerEl.querySelector(`[data-reply-edit-input="${replyId}"]`);
            try {
                state.detailItem = await state.api.updateReply(item.id, replyId, input.value.trim());
                state.editingReplyId = null;
                state.replyError = null;
                renderDetail();
            } catch (e) {
                state.replyError = e.message;
                renderDetail();
            }
        });
    });
    containerEl.querySelectorAll('[data-confirm-delete-reply]').forEach((el) => {
        el.addEventListener('click', () => {
            state.confirmingDeleteReplyId = Number(el.dataset.confirmDeleteReply);
            renderDetail();
        });
    });
    containerEl.querySelectorAll('[data-cancel-delete-reply]').forEach((el) => {
        el.addEventListener('click', () => {
            state.confirmingDeleteReplyId = null;
            renderDetail();
        });
    });
    containerEl.querySelectorAll('[data-do-delete-reply]').forEach((el) => {
        el.addEventListener('click', async () => {
            const replyId = Number(el.dataset.doDeleteReply);
            try {
                state.detailItem = await state.api.deleteReply(item.id, replyId);
                state.confirmingDeleteReplyId = null;
                renderDetail();
            } catch (e) {
                state.replyError = e.message;
                renderDetail();
            }
        });
    });
}

// item = null when creating (user mode only), the item being edited
// otherwise. Both share one form; only the copy, prefilled values and the
// Delete link differ.
function openForm(item = null) {
    state.editingItem = item;
    state.confirmingDelete = false;
    state.error = null;
    renderForm(item ? item.type : 'bug');
}

function renderForm(selectedType) {
    const { containerEl } = state;
    const item = state.editingItem;
    const isEdit = !!item;

    containerEl.innerHTML = `
        <div class="feedback-back-row">
            <button class="feedback-back-btn" data-back aria-label="Back"><i class="fa-solid fa-chevron-left"></i></button>
            <h3 class="modal-title" style="margin-bottom: 0;">${isEdit ? 'Edit feedback' : 'New feedback'}</h3>
        </div>
        <div class="feedback-scroll">
            <div class="feedback-error" id="feedbackFormError" style="${state.error ? '' : 'display: none;'}">${state.error ? escapeHtml(state.error) : ''}</div>
            <div class="feedback-field">
                <span class="feedback-field-label">What kind of feedback is this?</span>
                <div class="feedback-tabs">
                    <button class="feedback-tab ${selectedType === 'bug' ? 'active' : ''}" data-type="bug">Bug</button>
                    <button class="feedback-tab ${selectedType === 'feature' ? 'active' : ''}" data-type="feature">Feature</button>
                </div>
            </div>
            <div class="feedback-field">
                <label class="feedback-field-label" for="feedbackTitleInput">Title</label>
                <input id="feedbackTitleInput" class="feedback-input" type="text" maxlength="120"
                    placeholder="Short summary, e.g. &quot;Add sound on bust&quot;"
                    value="${isEdit ? escapeHtml(item.title) : ''}">
            </div>
            <div class="feedback-field">
                <label class="feedback-field-label" for="feedbackBodyInput">Description</label>
                <textarea id="feedbackBodyInput" class="feedback-textarea" maxlength="2000"
                    placeholder="What happened, or what would you like to see? Steps to reproduce help a lot for bugs."
                >${isEdit ? escapeHtml(item.body) : ''}</textarea>
            </div>
            ${state.mode === 'user' ? '<div class="feedback-subtitle">This post is linked to your device, so you can edit or remove it later &mdash; no account needed.</div>' : ''}
            <div class="feedback-form-actions">
                <button class="feedback-btn-primary btn-block" data-submit>${isEdit ? 'Save changes' : 'Submit feedback'}</button>
                <button class="feedback-cancel-link" data-back>Cancel</button>
                ${isEdit && state.mode === 'user' ? '<button class="feedback-delete-link" data-confirm-delete>Delete this feedback</button>' : ''}
            </div>
            ${state.confirmingDelete ? `
                <div class="feedback-confirm">
                    <div class="feedback-confirm-title">Delete this feedback?</div>
                    <div class="feedback-confirm-body">This can't be undone.</div>
                    <div class="feedback-confirm-actions">
                        <button class="feedback-btn-secondary" data-cancel-delete>Cancel</button>
                        <button class="btn-danger" data-do-delete>Delete</button>
                    </div>
                </div>
            ` : ''}
        </div>
    `;

    let currentType = selectedType;
    const typeButtons = containerEl.querySelectorAll('[data-type]');
    typeButtons.forEach((el) => {
        el.addEventListener('click', () => {
            // Toggles the active tab in place rather than re-rendering the
            // whole form (as everything else here does on a state change) -
            // a re-render would rebuild the title/body inputs from `item`
            // (or blank, for a new post), throwing away whatever the user
            // had already typed just for switching Bug/Feature.
            currentType = el.dataset.type;
            typeButtons.forEach((btn) => btn.classList.toggle('active', btn.dataset.type === currentType));
        });
    });
    containerEl.querySelectorAll('[data-back]').forEach((el) => {
        el.addEventListener('click', () => (isEdit ? openDetail(item.id) : renderList()));
    });
    containerEl.querySelector('[data-submit]').addEventListener('click', () => submitForm(currentType));

    const confirmBtn = containerEl.querySelector('[data-confirm-delete]');
    if (confirmBtn) confirmBtn.addEventListener('click', () => { state.confirmingDelete = true; renderForm(currentType); });
    const cancelBtn = containerEl.querySelector('[data-cancel-delete]');
    if (cancelBtn) cancelBtn.addEventListener('click', () => { state.confirmingDelete = false; renderForm(currentType); });
    const doDeleteBtn = containerEl.querySelector('[data-do-delete]');
    if (doDeleteBtn) doDeleteBtn.addEventListener('click', async () => {
        try {
            await state.api.deleteFeedbackItem(item.id);
            await renderList();
        } catch (e) {
            setFormError(e.message);
        }
    });
}

// Sets the form's error message in place, without re-rendering the rest of
// the form - a re-render would rebuild the title/body inputs from the
// original item (or blank, for a new post), throwing away whatever the user
// had typed right when a validation error is the one time they need it kept.
function setFormError(message) {
    state.error = message;
    const el = document.getElementById('feedbackFormError');
    if (!el) return;
    el.textContent = message || '';
    el.style.display = message ? '' : 'none';
}

async function submitForm(type) {
    const title = document.getElementById('feedbackTitleInput').value.trim();
    const bodyText = document.getElementById('feedbackBodyInput').value.trim();
    const item = state.editingItem;

    try {
        if (item) {
            await state.api.updateFeedbackItem(item.id, { type, title, body: bodyText });
            await openDetail(item.id);
        } else {
            const created = await state.api.createFeedbackItem({ type, title, body: bodyText });
            await openDetail(created.id);
        }
    } catch (e) {
        setFormError(e.message);
    }
}

// Exported for #-hash routing (see mountFeedbackBoard's initialItemId/
// onNavigate doc comment above) - a hashchange the caller didn't cause
// itself (a manual edit, or the browser back/forward button) needs to
// drive the board to the matching view the same way a click would.
export { openDetail, renderList };
